import { merge } from "lodash-es";
import React, { ComponentType, ReactElement } from "react";
import ReactDOMServer from "react-dom/server";

// Type for valid PDF paper formats
export type PdfFormat =
  | "a0"
  | "a1"
  | "a2"
  | "a3"
  | "a4"
  | "a5"
  | "a6"
  | "a7"
  | "a8"
  | "a9"
  | "a10"
  | "b0"
  | "b1"
  | "b2"
  | "b3"
  | "b4"
  | "b5"
  | "b6"
  | "b7"
  | "b8"
  | "b9"
  | "b10"
  | "c0"
  | "c1"
  | "c2"
  | "c3"
  | "c4"
  | "c5"
  | "c6"
  | "c7"
  | "c8"
  | "c9"
  | "c10"
  | "dl"
  | "letter"
  | "government-letter"
  | "legal"
  | "junior-legal"
  | "ledger"
  | "tabloid";

// Type for valid PDF measurement units
export type PdfUnit = "pt" | "px" | "mm" | "cm" | "in";

export type MarginList = [
  inches: number, // top
  inches: number, // left
  inches: number, // bottom
  inches: number, // right
];

// Interface for html2pdf instance
export interface Html2PdfInstance {
  (options?: PdfOptions): Html2PdfInstance;
  from: (element: HTMLElement | string) => Html2PdfInstance;
  set: (options: PdfOptions) => Html2PdfInstance;
  save: () => Promise<void>;
  outputPdf: (type: string) => Promise<Blob>;
  toPdf: () => Html2PdfInstance;
  get: (type: string) => Html2PdfInstance; // Returns Html2PdfInstance for chaining
  then: (callback: (pdf: any) => void) => Html2PdfInstance; // Callback doesn't return Html2PdfInstance
}

// Cached instance of html2pdf module
let html2pdfInstance: Html2PdfInstance | null = null;

export type PdfOrientation = "portrait" | "landscape";

// Interface for PDF generation options
export interface PdfOptions {
  margin?: MarginList;
  orientation?: PdfOrientation;
  format?: PdfFormat;
  unit?: PdfUnit;
  filename?: string;
  image?: {
    type?: "jpeg" | "png" | "webp";
    quality?: number;
  };
  pagebreak?: {
    mode?: ("avoid" | "css" | "legacy")[];
    before?: string[];
    after?: string[];
    avoid?: string[];
  };
  html2canvas?: {
    scale?: number;
    useCORS?: boolean;
    letterRendering?: boolean;
    allowTaint?: boolean; // Help with rendering
    foreignObjectRendering?: boolean; // Better text rendering
    height?: number | null;
    width?: number | null;
    scrollX?: number;
    scrollY?: number;
  };
  jsPDF?: {
    autoPaging?: "text";
    unit?: PdfUnit;
    format?: PdfFormat;
    orientation?: "portrait" | "landscape";
    compress?: boolean; // Enable compression for smaller file size
    precision?: number; // Precision for text rendering
  };
}

/**
 * Generates a PDF from a React component
 * @param Component The React component to render to PDF
 * @param props The props to pass to the component
 * @param selectedOptions PDF generation options
 * @param callback Optional callback to handle the generated PDF
 * @returns Promise<Blob> The generated PDF as a Blob
 */
export async function generatePDF<P extends object>(
  Component: ComponentType<P>,
  props: P,
  selectedOptions: PdfOptions = {},
  callback: (pdf: any) => void = (_) => {
    /* no-op */
  },
): Promise<Blob> {
  if (!html2pdfInstance) {
    // Dynamically import html2pdf because it uses native web apis which are not
    // available when Next.js prerenders the page on the server.
    // See https://stackoverflow.com/a/66100185
    const { default: html2pdf } = await import("html2pdf.js" as string);
    html2pdfInstance = html2pdf;
  }

  if (!html2pdfInstance) {
    throw new Error("Failed to load html2pdf library");
  }

  try {
    // Create a React element and render it to a string
    const component: ReactElement = React.createElement(Component, props);
    const htmlString = ReactDOMServer.renderToString(component);

    // Create a container and set its content
    const container = document.createElement("div");
    container.innerHTML = htmlString;

    replaceFormFieldsWithDivs(container);

    // Default PDF settings that match the designs
    const defaultOptions: PdfOptions = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      image: { type: "jpeg" as const, quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        allowTaint: true, // Help with rendering
        height: null, // Let it calculate natural height
        width: null, // Let it calculate natural width
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: {
        unit: selectedOptions.unit || "mm",
        format: selectedOptions.format || "letter",
        orientation: selectedOptions.orientation || "portrait",
        compress: true, // Better compression
        precision: 2,
      },
    };

    // Merge user options with defaults using lodash-es merge for deep merging
    const mergedOptions = merge({}, defaultOptions, selectedOptions);

    return await html2pdfInstance()
      .from(container)
      .set(mergedOptions)
      .toPdf()
      .get("pdf")
      .then(callback)
      .outputPdf("blob");
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
}

function replaceFormFieldsWithDivs(container: HTMLElement) {
  // Select all text areas and relevant input types
  const fields = container.querySelectorAll(
    'textarea, input[type="text"], input[type="number"], input[type="email"], input[type="search"], input[type="tel"], input[type="url"], input[type="password"]',
  );

  fields.forEach((field) => {
    // Create replacement div with same styling
    const replacementDiv = document.createElement("div");
    replacementDiv.className = (field as HTMLElement).className;
    replacementDiv.style.cssText = (field as HTMLElement).style.cssText;

    // Ensure text wrapping is preserved
    replacementDiv.style.whiteSpace = "pre-wrap";
    replacementDiv.style.wordBreak = "break-word";
    replacementDiv.style.border = (field as HTMLElement).style.border;
    replacementDiv.style.padding = (field as HTMLElement).style.padding;
    replacementDiv.style.minHeight = (field as HTMLElement).style.height;
    replacementDiv.style.width = (field as HTMLElement).style.width;

    // If the field is an input, add padding to the top
    if (field.tagName.toLowerCase() === "input") {
      // Add 6px top padding, or increase existing top padding by 6px if present
      const currentPadding = window.getComputedStyle(field).paddingTop;
      const numericPadding = parseInt(currentPadding, 10) || 0;
      replacementDiv.style.paddingTop = `${numericPadding + 6}px`;
    }

    // Use type assertion to access .value safely
    let value = "";
    if (
      field instanceof HTMLInputElement ||
      field instanceof HTMLTextAreaElement
    ) {
      value = field.value;
    } else {
      value = field.textContent || "";
    }
    replacementDiv.textContent = value;

    // Replace the textarea and/or input with our div
    field.parentNode?.replaceChild(replacementDiv, field);
  });
}
